/**
 * arloSignals.js - Build the signal arrays sent to Arlo.
 *
 * Shared by useArloRecommendations (rails) and useAskArlo (chat): reactions
 * come from localStorage (reaction_<profileId>_<channelId> keys), favorites
 * from the watchlist, and `watched` from resumptions. likes/dislikes/favorites
 * are {id: slug, title} pairs per Arlo's signals rule — bare id strings are
 * deprecated back-compat. `watched` is the richer
 * {id, title, progress, completed, last_watched_at} shape.
 */
import { userDataService } from '../services/UserDataService';

// Arlo caps `watched` at 50 server-side and takes the most recent; slice here
// so the cut is ours and predictable rather than wherever the server truncates.
const WATCHED_LIMIT = 50;

// A finished watch is taste input, not an endorsement — Arlo weights it, it is
// never sent as a `like`. 0.95 matches the threshold in Arlo's own doc example.
const COMPLETED_AT = 0.95;

export const readStoredReactions = (profileId, byId) => {
  const likes = [];
  const dislikes = [];

  if (!profileId) return { likes, dislikes };

  const prefix = `reaction_${profileId}_`;
  Object.keys(localStorage)
    .filter(key => key.startsWith(prefix))
    .forEach(key => {
      const channelId = key.slice(prefix.length);
      const channel = byId.get(channelId);
      if (!channel?.slug) return;

      let action;
      try {
        action = JSON.parse(localStorage.getItem(key));
      } catch (e) {
        return;
      }

      if (action === 'like' || action === 'double_like') {
        likes.push({ id: channel.slug, title: channel.title || channel.slug });
      } else if (action === 'dislike' || action === 'double_dislike') {
        dislikes.push({ id: channel.slug, title: channel.title || channel.slug });
      }
    });

  return { likes, dislikes };
};

export const fetchWatchlistFavorites = async () => {
  const watchlistChannels = await userDataService.getWatchlist();
  return (watchlistChannels || [])
    .filter(channel => channel?.slug)
    .map(channel => ({ id: channel.slug, title: channel.title || channel.slug }));
};

/**
 * Resumptions → Arlo's `watched` signal.
 *
 * Both resumption buckets count as viewing history: 'continue-watching' is
 * in-progress, 'watch-again' is finished. Ids are resolved through the same
 * byId lookup the rails use, so a slug we send is always a slug Arlo can match
 * back to this catalog — a resumption whose channel isn't in the payload is
 * dropped rather than guessed at.
 *
 * For a series the parent channel is the taste-bearing thing (ChillFree only
 * ever renders the parent tile), so episodes collapse onto it — most recent
 * wins, which also dedupes a show watched across several episodes.
 */
export const fetchWatchedSignals = async byId => {
  // Mirrors useContinueWatching: the cached resumptions may not be populated
  // yet when this runs, and an empty `watched` would silently cost the user
  // personalization rather than fail loudly.
  await userDataService.refreshResumptionsData();

  const buckets = [
    { items: userDataService.getContinueWatching() || [], finished: false },
    { items: userDataService.getWatchAgain() || [], finished: true },
  ];

  const byContentId = new Map();

  buckets.forEach(({ items, finished }) => {
    items.forEach(item => {
      const channelId = item?.parent_channel?._id || item?.channel?._id;
      const channel = channelId && byId.get(channelId);
      if (!channel?.slug) return;

      const progress =
        item.duration > 0 && typeof item.point === 'number'
          ? item.point / item.duration
          : undefined;

      const signal = {
        id: channel.slug,
        title: channel.title || item.seriestitle || item.title || channel.slug,
        completed: finished || (progress !== undefined && progress >= COMPLETED_AT),
        last_watched_at: item.updatedAt,
      };
      if (progress !== undefined) signal.progress = progress;

      const existing = byContentId.get(signal.id);
      if (existing && new Date(existing.last_watched_at || 0) >= new Date(signal.last_watched_at || 0)) {
        // Keep the newer entry, but never downgrade a finished watch to unfinished.
        if (signal.completed) existing.completed = true;
        return;
      }
      if (existing?.completed) signal.completed = true;
      byContentId.set(signal.id, signal);
    });
  });

  return [...byContentId.values()]
    .sort((a, b) => new Date(b.last_watched_at || 0) - new Date(a.last_watched_at || 0))
    .slice(0, WATCHED_LIMIT);
};
